import type { DeliveryStatusEnumKey } from '../api/generated/models/DeliveryStatus';

/**
 * `reason` values carried on a `message.deleted` timeline event. Canonical vocabulary — never inline
 * the literal. `EchoAbsorbedIntoOutbound` marks an Instagram/Messenger `is_echo` that the backend
 * reconciled into our own outbound message; the thread drops its transient empty bubble live.
 *
 * Mirror of `automato-api` `MESSAGE_DELETED_REASON` (conversation.constants.ts) — the two repos share
 * no package, so keep both sides in sync when changing a value.
 */
export const MESSAGE_DELETED_REASON = {
  EchoAbsorbedIntoOutbound: 'echo_absorbed_into_outbound',
} as const;

/** Realtime socket event names emitted by the backend conversation gateway. */
export const CONVERSATION_SOCKET_EVENTS = {
  messageNew: 'message:new',
  messageUpdated: 'message:updated',
  messageDelivery: 'message:delivery',
  conversationNew: 'conversation:new',
  conversationUpdated: 'conversation:updated',
  conversationAssigned: 'conversation:assigned',
  conversationResolved: 'conversation:resolved',
  conversationArchived: 'conversation:archived',
  /** Append-only timeline event landed on `conversation_events`. */
  conversationEvent: 'conversation:event',
  /** Provider reply-window (e.g. WhatsApp 24h) opened or closed for a conversation. */
  conversationReplyWindowChanged: 'conversation:reply-window-changed',
} as const;

export interface MessageNewEvent {
  conversationId: string;
  messageId?: string;
  /** Shared by every part of a multi-part agent response. Adjacent bubbles
   * with the same responseGroupId can be visually grouped by the store. */
  responseGroupId?: string;
  /** 0-based position of this part inside its response group. */
  partIndex?: number;
}

export interface MessageUpdatedEvent {
  conversationId: string;
  messageId?: string;
}

export interface MessageDeliveryEvent {
  conversationId: string;
  messageId?: string;
  deliveryStatus?: DeliveryStatusEnumKey;
  /** Motivo instrutivo da falha (só em `failed`) — para o tooltip persistente e o toast ao vivo. */
  failureReason?: string | null;
}

export interface ConversationChangedEvent {
  conversationId: string;
}

/** Snapshot of the reply window state for a conversation. */
export interface ConversationReplyWindowChangedEvent {
  conversationId: string;
  open: boolean;
  expiresAt: string | null;
  reopenWith: 'template' | null;
}

/** Envelope pushed for every new conversation_events row. snake_case mirrors the outbox payload. */
export interface ConversationEventAppendedPayload {
  conversation_id: string;
  business_id: string;
  event_id: string;
  event_type: string;
  session_id: string | null;
  actor_type: 'membership' | 'agent' | 'service_account' | 'system' | 'contact';
  actor_id: string | null;
  occurred_at: string;
  payload: Record<string, unknown>;
}
